import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import { toast } from 'react-hot-toast';
import { uploadKYCFile, validateFile, formatFileSize } from '../../services/fileUploadService';

// =====================================================
// INTERFACES
// =====================================================

interface DriverApplication {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  address: string;
  city: string;
  license_number: string;
  license_expiry_date: string;
  license_type: string;
  years_driving_experience: number;
  status: 'pending' | 'under_review' | 'approved' | 'rejected' | 'kyc_required';
  application_date: string;
  kyc_documents?: KYCDocument[];
}

interface KYCDocument {
  id: string;
  document_type: 'id_document' | 'license_document' | 'proof_of_address';
  file_name: string;
  file_url: string;
  is_verified: boolean;
  created_at: string;
}

// =====================================================
// DRIVER APPLICATION FORM COMPONENT
// =====================================================

const DriverApplicationForm: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [checkingApplication, setCheckingApplication] = useState(true);
  const [existingApplication, setExistingApplication] = useState<DriverApplication | null>(null);
  const [currentStep, setCurrentStep] = useState<'form' | 'kyc' | 'submitted'>('form');

  // Form data
  const [formData, setFormData] = useState({
    fullName: user?.name || '',
    email: user?.email || '',
    phone: '',
    dateOfBirth: '',
    address: '',
    city: '',
    licenseNumber: '',
    licenseExpiryDate: '',
    licenseType: 'B',
    yearsDrivingExperience: 0
  });

  // KYC upload data
  const [kycFiles, setKycFiles] = useState({
    idDocument: null as File | null,
    licenseDocument: null as File | null,
    proofOfAddress: null as File | null
  });

  useEffect(() => {
    checkExistingApplication();
  }, []);

  const checkExistingApplication = async () => {
    // PREVENT STATE OVERRIDE: Don't check if we already have application data from successful submission
    if (existingApplication) {
      console.log('🔄 Skipping application status check - already have application data from submission');
      setCheckingApplication(false);
      return;
    }

    console.log('🔄 Starting application status check...');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('❌ No auth token found');
        setCheckingApplication(false);
        return;
      }

      console.log('📡 Making API request to check application status...');
      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/my-application`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      console.log('📡 API Response received - Status:', response.status, 'OK:', response.ok);

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Existing application check result:', JSON.stringify(data, null, 2));

        if (data.application) {
          console.log('📋 Application found:', {
            id: data.application.id,
            status: data.application.status,
            full_name: data.application.full_name,
            created_at: data.application.application_date
          });

          setExistingApplication(data.application);

          // Navigate to dashboard instead of showing KYC page
          console.log('🎯 Existing application found - redirecting to dashboard');
          navigate('/driver-dashboard');
          return;
        } else {
          console.log('📝 No existing application found - User can submit new application');
          setExistingApplication(null);
          setCurrentStep('form');
        }
      } else {
        console.error('❌ API request failed - Status:', response.status);
        try {
          const errorData = await response.json();
          console.error('❌ Error response:', errorData);
        } catch (e) {
          console.error('❌ Could not parse error response');
        }
      }
    } catch (error) {
      console.error('❌ Exception during application check:', error);
    } finally {
      console.log('✅ Application status check completed');
      setCheckingApplication(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (documentType: keyof typeof kycFiles, file: File | null) => {
    setKycFiles(prev => ({
      ...prev,
      [documentType]: file
    }));
  };

  const uploadFile = async (file: File, documentType: string): Promise<{ filePath: string; fileUrl: string }> => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    // Validate file before upload
    const validation = validateFile(file);
    if (!validation.isValid) {
      throw new Error(validation.error || 'Invalid file');
    }

    // Upload to Supabase Storage
    const uploadResult = await uploadKYCFile({
      userId: user.id,
      documentType: documentType as 'id_document' | 'license_document' | 'proof_of_address',
      file: file
    });

    if (!uploadResult.success) {
      throw new Error(uploadResult.error || 'Upload failed');
    }

    return {
      filePath: uploadResult.filePath || '',
      fileUrl: uploadResult.fileUrl || ''
    };
  };

  const handleSubmitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('🚀 Submitting driver application...');
    console.log('Form data:', formData);

    // Basic form validation
    if (!formData.fullName.trim()) {
      toast.error('Please enter your full name');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('Please enter your phone number');
      return;
    }
    if (!formData.dateOfBirth) {
      toast.error('Please enter your date of birth');
      return;
    }
    if (!formData.address.trim()) {
      toast.error('Please enter your address');
      return;
    }
    if (!formData.city.trim()) {
      toast.error('Please enter your city');
      return;
    }
    if (!formData.licenseNumber.trim()) {
      toast.error('Please enter your license number');
      return;
    }
    if (!formData.licenseExpiryDate) {
      toast.error('Please enter your license expiry date');
      return;
    }
    if (formData.yearsDrivingExperience < 0) {
      toast.error('Please enter valid years of driving experience');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      console.log('Auth token present:', !!token);

      if (!token) {
        toast.error('You are not authenticated. Please log in again.');
        return;
      }

      // Ensure proper data types and formats for API
      const formattedData = {
        ...formData,
        yearsDrivingExperience: parseInt(formData.yearsDrivingExperience.toString(), 10),
        // Ensure dates are in ISO format
        dateOfBirth: new Date(formData.dateOfBirth).toISOString().split('T')[0],
        licenseExpiryDate: new Date(formData.licenseExpiryDate).toISOString().split('T')[0]
      };

      console.log('📝 Formatted data for API:', formattedData);

      const response = await fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/apply`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formattedData),
      });

      console.log('API Response status:', response.status);
      console.log('API Response ok:', response.ok);

      const responseData = await response.json();
      console.log('API Response data:', responseData);
      console.log('Response data keys:', Object.keys(responseData));
      console.log('Response has application_id:', !!responseData.application_id);
      console.log('Response application_id value:', responseData.application_id);

      // Handle existing application case - this should redirect to KYC
      if (!response.ok && response.status === 400 && responseData.message?.includes('already have a driver application')) {
        console.log('🎯 User already has application - redirecting to KYC upload');

        // The backend should return application data in this case
        if (responseData.application) {
          console.log('📋 Setting existing application from error response:', responseData.application);
          setExistingApplication(responseData.application);

          if (responseData.application.status === 'kyc_required') {
            console.log('🎯 Application requires KYC - redirecting to upload page');
            setCurrentStep('kyc');
            toast.success('You already have an application. Please upload your KYC documents.');
            return;
          } else {
            console.log('📄 Application in different status - showing status page');
            setCurrentStep('submitted');
            toast('You already have an application submitted.', { icon: 'ℹ️' });
            return;
          }
        }

        // If no application data in response, try to fetch it
        console.log('⚠️ No application data in error response, trying to fetch...');
        try {
          await checkExistingApplication();
          // If we now have application data, redirect appropriately
          if (existingApplication) {
            if (existingApplication.status === 'kyc_required') {
              setCurrentStep('kyc');
              toast.success('Redirecting to KYC upload...');
            } else {
              setCurrentStep('submitted');
              toast('You already have an application submitted.', { icon: 'ℹ️' });
            }
          } else {
            console.error('❌ Still no application data after fetch attempt');
            toast.error('Unable to find your application. Please contact support.');
          }
          return;
        } catch (fetchError) {
          console.error('❌ Failed to fetch application data:', fetchError);
          toast.error('Unable to access your application. Please contact support.');
          return;
        }
      }

      // Handle success responses
      if (response.ok && responseData.success) {
        console.log('✅ Application submitted successfully');
        console.log('📋 Full response data:', JSON.stringify(responseData, null, 2));

        // Set the application data directly from the response to avoid API timing issues
        if (responseData.application) {
          console.log('📋 Setting application data from responseData.application:', responseData.application);

          // Always ensure the application object has an ID - use top-level application_id if needed
          if (responseData.application_id) {
            responseData.application.id = responseData.application_id;
            console.log('📋 Set application ID from responseData.application_id:', responseData.application.id);
          } else if (!responseData.application.id) {
            console.error('❌ No application ID found in response - this should not happen');
            toast.error('Application submitted but ID not found. Please refresh and try again.');
            return;
          }

          setExistingApplication(responseData.application);
        } else if (responseData.application_id) {
          // Handle new application response format
          console.log('📋 Setting new application data from responseData.application_id:', responseData.application_id);
          const newApplication: DriverApplication = {
            id: responseData.application_id,
            user_id: user?.id || '', // Use actual user ID from context
            full_name: formData.fullName,
            email: formData.email,
            phone: formData.phone,
            date_of_birth: formData.dateOfBirth,
            address: formData.address,
            city: formData.city,
            license_number: formData.licenseNumber,
            license_expiry_date: formData.licenseExpiryDate,
            license_type: formData.licenseType,
            years_driving_experience: formData.yearsDrivingExperience,
            status: 'kyc_required',
            application_date: new Date().toISOString()
          };
          console.log('📋 Created new application object:', JSON.stringify(newApplication, null, 2));
          setExistingApplication(newApplication);
        } else {
          console.error('❌ No application data found in response');
          console.error('Response data keys:', Object.keys(responseData));
          toast.error('Application submitted but unable to proceed to KYC upload. Please refresh and try again.');
          return;
        }

        // Verify application data was set
        console.log('📋 Current existingApplication state should be set now');
        toast.success('Application submitted successfully! Redirecting to dashboard...');
        navigate('/driver-dashboard');
        return;
      }

      // Handle other error responses
      console.error('❌ API Error - Response not ok - Status:', response.status);
      let errorMessage = 'Failed to submit application';

      if (responseData.details) {
        console.error('❌ Validation errors details:', responseData.details);
        const validationErrors = responseData.details.map((d: any) => `${d.param}: ${d.msg}`).join(', ');
        errorMessage = `Validation errors: ${validationErrors}`;
        console.error('❌ Formatted validation errors:', errorMessage);
      } else if (responseData.message) {
        errorMessage = responseData.message;
        console.error('❌ Error message:', errorMessage);
      } else if (responseData.error) {
        errorMessage = responseData.error;
        console.error('❌ Error field:', errorMessage);
      }

      toast.error(errorMessage);

    } catch (error) {
      console.error('❌ Exception during application submission:', error);
      toast.error('Failed to submit application. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadKYC = async () => {
    console.log('🚀 Starting KYC document upload process...');

    if (!existingApplication) {
      console.error('❌ No existing application found');
      toast.error('Application not found. Please refresh the page.');
      return;
    }

    // Use application ID directly - ensure it exists
    let applicationId = existingApplication.id;

    // If application ID is missing or invalid, use a temporary session ID that the server can resolve
    if (!applicationId || applicationId.trim() === '') {
      console.warn('⚠️ Application ID missing or invalid from existingApplication, using temp session ID');
      applicationId = `temp-kyc-session-${user?.id}`;
      console.log('📋 Using temporary application ID:', applicationId);
    } else {
      console.log('📋 Using real application ID:', applicationId);
    }

    console.log('📋 Using application ID:', applicationId);
    console.log('📋 Existing application:', {
      id: applicationId,
      status: existingApplication.status,
      user_id: existingApplication.user_id
    });

    // Validate that all required documents are selected
    const requiredDocs = ['idDocument', 'licenseDocument', 'proofOfAddress'];
    const missingDocs = requiredDocs.filter(docType => !kycFiles[docType as keyof typeof kycFiles]);

    if (missingDocs.length > 0) {
      console.error('❌ Missing required documents:', missingDocs);
      toast.error('Please upload all required documents: ID Document, License Document, and Proof of Address');
      return;
    }

    console.log('✅ All required documents selected:', Object.keys(kycFiles).filter(key => kycFiles[key as keyof typeof kycFiles]));

    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('❌ No authentication token found');
        toast.error('Authentication token not found. Please log in again.');
        return;
      }

      console.log('🔐 Authentication token present');

      const uploadPromises: Promise<Response>[] = [];
      const uploadedFiles: any[] = [];

      // Upload each file if selected
      for (const [docType, file] of Object.entries(kycFiles)) {
        if (file) {
          try {
            console.log(`📤 Starting upload for ${docType}:`, {
              fileName: file.name,
              fileSize: file.size,
              fileType: file.type,
              fileSizeFormatted: `${(file.size / 1024 / 1024).toFixed(2)} MB`
            });

            const uploadResult = await uploadFile(file, docType);
            console.log(`✅ Upload result for ${docType}:`, JSON.stringify(uploadResult, null, 2));

            // Store uploaded file info for API submission
            uploadedFiles.push({
              docType,
              file,
              uploadResult
            });

            // Map frontend document types to backend expected values
            const documentTypeMap: { [key: string]: string } = {
              'idDocument': 'id_document',
              'licenseDocument': 'license_document',
              'proofOfAddress': 'proof_of_address'
            };

            const requestData = {
              applicationId: applicationId, // Use the validated application ID
              documentType: documentTypeMap[docType] || docType, // Use mapped value
              fileName: file.name,
              fileUrl: uploadResult.filePath, // Send file path, not URL
              fileSize: file.size,
              mimeType: file.type
            };

            console.log(`📡 Preparing API request for ${docType}:`, JSON.stringify(requestData, null, 2));

            // Double-check applicationId is in requestData
            if (!requestData.applicationId) {
              console.error('❌ CRITICAL: applicationId is missing from requestData!', requestData);
              toast.error('Application ID missing from request. Please refresh and try again.');
              return;
            }

            const requestBody = JSON.stringify(requestData);
            console.log(`📡 Final request body for ${docType}:`, requestBody);

            uploadPromises.push(
              fetch(`${API_ENDPOINTS.BASE}/api/driver-applications/kyc/upload`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
                body: requestBody,
              })
            );
          } catch (uploadError) {
            console.error(`❌ Error uploading ${docType} to storage:`, uploadError);
            toast.error(`Failed to upload ${docType}: ${uploadError instanceof Error ? uploadError.message : 'Unknown error'}`);
            return;
          }
        }
      }

      if (uploadPromises.length === 0) {
        console.error('❌ No upload promises created');
        toast.error('Please select at least one document to upload');
        return;
      }

      console.log(`📤 Submitting ${uploadPromises.length} documents to database...`);

      const responses = await Promise.all(uploadPromises);

      console.log('📡 All upload API responses:', responses.map((r, index) => ({
        index,
        status: r.status,
        statusText: r.statusText,
        ok: r.ok,
        url: r.url
      })));

      // Check if all responses are ok
      const failedResponses = responses.filter(r => !r.ok);
      if (failedResponses.length > 0) {
        console.error('❌ Some uploads failed:', failedResponses.map(r => ({
          status: r.status,
          statusText: r.statusText,
          url: r.url
        })));

        // Try to get error details from failed responses
        for (let i = 0; i < failedResponses.length; i++) {
          const response = failedResponses[i];
          try {
            const errorData = await response.json();
            console.error(`❌ Error response data for request ${i}:`, JSON.stringify(errorData, null, 2));
            console.error('Error details:', errorData.error);
            console.error('Error message:', errorData.message);
            if (errorData.details) {
              console.error('Validation details:', JSON.stringify(errorData.details, null, 2));
            }
          } catch (e) {
            console.error(`❌ Could not parse error response for request ${i}:`, e);
            try {
              const errorText = await response.text();
              console.error(`❌ Raw error response text for request ${i}:`, errorText);
            } catch (textError) {
              console.error(`❌ Could not read error response as text for request ${i}:`, textError);
            }
          }
        }

        toast.error(`${failedResponses.length} document(s) failed to upload to database. Please try again.`);
        return;
      }

      console.log('✅ All API responses successful, parsing results...');

      const results = await Promise.all(responses.map(async (r, index) => {
        try {
          const data = await r.json();
          console.log(`✅ Response data for request ${index}:`, JSON.stringify(data, null, 2));
          return data;
        } catch (e) {
          console.error(`❌ Could not parse response ${index} as JSON:`, e);
          return { success: false, error: 'Invalid response format' };
        }
      }));

      const allSuccessful = results.every(r => r.success !== false);
      console.log('📊 Upload results summary:', {
        totalRequests: results.length,
        successful: results.filter(r => r.success !== false).length,
        failed: results.filter(r => r.success === false).length,
        allSuccessful
      });

      if (allSuccessful) {
        console.log('🎉 All documents uploaded successfully to database!');
        toast.success('KYC documents uploaded successfully! Your application is now under review.');
        setCurrentStep('submitted');
        await checkExistingApplication();
      } else {
        const failedResults = results.filter(r => r.success === false);
        console.error('❌ Some uploads failed at database level:', failedResults);
        toast.error('Some documents failed to save to database. Please try again.');
      }
    } catch (error) {
      console.error('❌ Exception during KYC document upload:', error);
      toast.error('Failed to upload KYC documents. Please check your connection and try again.');
    } finally {
      setLoading(false);
      console.log('🏁 KYC upload process completed');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'under_review':
        return 'bg-blue-100 text-blue-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'kyc_required':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusMessage = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Your application is pending review by our admin team.';
      case 'under_review':
        return 'Your application is currently being reviewed.';
      case 'approved':
        return 'Congratulations! Your driver application has been approved.';
      case 'rejected':
        return 'Your application was rejected. Please contact support for more information.';
      case 'kyc_required':
        return 'Please upload your KYC documents to complete your application.';
      default:
        return 'Unknown status';
    }
  };

  if (existingApplication && currentStep === 'submitted') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card-highlighted">
          <div className="text-center">
            <div className="mb-4">
              <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(existingApplication.status)}`}>
                {existingApplication.status.replace('_', ' ').toUpperCase()}
              </span>
            </div>
            <h2 className="text-2xl font-bold text-primary-800 mb-2">
              Driver Application Status
            </h2>
            <p className="text-primary-600 mb-6">
              {getStatusMessage(existingApplication.status)}
            </p>

            {existingApplication.status === 'kyc_required' && (
              <button
                onClick={() => setCurrentStep('kyc')}
                className="btn-primary"
              >
                Upload KYC Documents
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (currentStep === 'kyc') {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card-highlighted">
          <h2 className="text-2xl font-bold text-primary-800 mb-6">
            Upload KYC Documents
          </h2>
          <p className="text-primary-600 mb-6">
            Please upload the following documents to complete your driver application:
          </p>

          <div className="space-y-6">
            {/* ID Document */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                National ID or Passport *
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange('idDocument', e.target.files?.[0] || null)}
                className="input-field"
              />
              {kycFiles.idDocument && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">
                    Selected: {kycFiles.idDocument.name} ({formatFileSize(kycFiles.idDocument.size)})
                  </p>
                </div>
              )}
              <p className="text-xs text-primary-500 mt-1">
                Upload a clear photo or scan of your ID document (JPEG, PNG, or PDF, max 10MB)
              </p>
            </div>

            {/* License Document */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Driver's License *
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange('licenseDocument', e.target.files?.[0] || null)}
                className="input-field"
              />
              {kycFiles.licenseDocument && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">
                    Selected: {kycFiles.licenseDocument.name} ({formatFileSize(kycFiles.licenseDocument.size)})
                  </p>
                </div>
              )}
              <p className="text-xs text-primary-500 mt-1">
                Upload a clear photo or scan of your driver's license (JPEG, PNG, or PDF, max 10MB)
              </p>
            </div>

            {/* Proof of Address */}
            <div>
              <label className="block text-sm font-medium text-primary-700 mb-2">
                Proof of Address *
              </label>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => handleFileChange('proofOfAddress', e.target.files?.[0] || null)}
                className="input-field"
              />
              {kycFiles.proofOfAddress && (
                <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
                  <p className="text-sm text-green-800">
                    Selected: {kycFiles.proofOfAddress.name} ({formatFileSize(kycFiles.proofOfAddress.size)})
                  </p>
                </div>
              )}
              <p className="text-xs text-primary-500 mt-1">
                Upload a utility bill or bank statement (not older than 3 months, JPEG, PNG, or PDF, max 10MB)
              </p>
            </div>
          </div>

          <div className="flex gap-4 mt-8">
            <button
              onClick={() => setCurrentStep('form')}
              className="btn-secondary flex-1"
            >
              Back to Application
            </button>
            <button
              onClick={handleUploadKYC}
              disabled={loading || !kycFiles.idDocument || !kycFiles.licenseDocument || !kycFiles.proofOfAddress}
              className="btn-primary flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Uploading...' : 'Upload Documents'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Show loading while checking for existing applications
  if (checkingApplication) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="card-highlighted">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            <span className="ml-3 text-primary-600">Checking application status...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="card-highlighted">
        {existingApplication ? (
          <>
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">
                    Application Already Submitted
                  </h3>
                  <p className="text-sm text-blue-700 mt-1">
                    You have already submitted a driver application. Please check your application status or contact support if you need assistance.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex justify-center">
              <button
                onClick={() => window.location.reload()}
                className="btn-secondary"
              >
                Check Status
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold text-primary-800 mb-6">
              Apply to Become a Driver
            </h2>
            <p className="text-primary-600 mb-6">
              Fill out the form below to apply as a driver. You'll need to upload your KYC documents after submission.
            </p>

            <form onSubmit={handleSubmitApplication} className="space-y-6">
              {/* Personal Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  />
                </div>
              </div>

              {/* Address Information */}
              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-primary-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  className="input-field"
                />
              </div>

              {/* License Information */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    License Number *
                  </label>
                  <input
                    type="text"
                    name="licenseNumber"
                    value={formData.licenseNumber}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    License Expiry Date *
                  </label>
                  <input
                    type="date"
                    name="licenseExpiryDate"
                    value={formData.licenseExpiryDate}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    License Type *
                  </label>
                  <select
                    name="licenseType"
                    value={formData.licenseType}
                    onChange={handleInputChange}
                    required
                    className="input-field"
                  >
                    <option value="A">A - Motorcycle</option>
                    <option value="B">B - Light Vehicle</option>
                    <option value="C">C - Medium Vehicle</option>
                    <option value="D">D - Heavy Vehicle</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-primary-700 mb-2">
                    Years of Driving Experience *
                  </label>
                  <input
                    type="number"
                    name="yearsDrivingExperience"
                    value={formData.yearsDrivingExperience}
                    onChange={handleInputChange}
                    min="0"
                    required
                    className="input-field"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default DriverApplicationForm;
